// The OS-wide notes dock (S13) — Grant's framing: "FOR NOTES OS WIDE
// FOR EVERYONE."
//
// Notes stop being an in-page card that each record page mounted in its
// own spot, and become a chrome-tab-sized tab docked at the bottom of
// the screen. Clicked, it expands upward into two panes: LEFT to write,
// RIGHT the searchable history. Same data, same endpoints, same PII
// warning, same voice composer, same follow-up chips — new chrome.
//
// Three deliberate departures from the Ask Stew panel it borrows its
// geometry from:
//
//   1. NOT modal. No scrim, no aria-modal, no focus trap. Stew is a
//      conversation you're having INSTEAD of using the app; notes are
//      something you jot WHILE using it — you should be able to click a
//      nav item or scroll the record with the dock open. This is also
//      what the design doc means by "spanning the content width but not
//      the left nav" (§9): the nav stays both visible AND usable.
//   2. Docked left edge follows --nav-col-w, so it lines up with the
//      content column whether the nav rail is open (288px), collapsed
//      (69px), or hidden on a phone (0). See the variable's definition
//      in styles.css for why it exists.
//   3. Its own state, not ChromeContext's. The dock is per-record — it
//      closes when you navigate to a different record — so its open flag
//      belongs with the component that knows the record, not in app-wide
//      chrome state that would have to be reset from outside.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { ChevronDown, NotebookPen, Search, X } from "lucide-react";

import {
  api,
  ApiError,
  NOTE_KIND_BY_SEGMENT,
  newNoteId,
  noteSaveFailureMessage,
  type FollowupSuggestion,
  type HouseholdNoteKind,
  type Note,
} from "../../api";
import type { NoteEntitySegment } from "@shared/types/notes";
import { Button } from "../../ui";
import { useSession } from "../../session";
import { NotePiiWarning, draftHasPii } from "../NoteSurfaceHelpers";
import {
  HouseholdNoteRow,
  RelatedHouseholdNotes,
  useHouseholdNotes,
  useHouseholdNoteTotal,
} from "../HouseholdNotes";
import { useUserSetting } from "../../settings/user-settings-context";
import { NOTES_LATEST_AT_BOTTOM } from "../../settings/user-settings-registry";
import { resolveLatestNotesAtBottom } from "./latest-notes-default";
import { VoiceNoteComposer } from "../VoiceNoteComposer";
import { FollowupSuggestionChips } from "../ai/FollowupSuggestionChips";
import { NoteRow } from "./NoteRow";
import { TOUCH_OPTIONS, TOUCH_RECORD_BY_SEGMENT } from "./AddNoteModal";
import { emitNotesChanged, subscribeNotesChanged } from "./notes-changed-event";
import { useDock } from "../docks/dock-state";
import { useToast } from "../Toast";

/** Plain-English name for the record, for the composer's placeholder and
 *  the empty state. Keyed on the API segment so it can't drift from the
 *  kind actually being written to. */
const ENTITY_LABEL: Record<NoteEntitySegment, string> = {
  households: "this household",
  contacts: "this person",
  "service-records": "this record",
  opportunities: "this opportunity",
  transactions: "this processing record",
  "rhythm-instances": "this care plan cycle",
};

// The half-typed note is kept in this tab (sessionStorage — the home the
// localStorage rule allows for an in-progress composer draft) so a reload
// or a dropped connection can't take it (Nate, 2026-09-22: "notes NEED to
// save"). Keyed on the record, so a draft never follows you to another one.
function dockDraftKey(kind: NoteEntitySegment, entityId: string): string {
  return `notes-dock-draft:${kind}:${entityId}`;
}

function readDockDraft(key: string): string {
  try {
    return window.sessionStorage.getItem(key) ?? "";
  } catch {
    return "";
  }
}

function writeDockDraft(key: string, value: string): void {
  try {
    if (value) window.sessionStorage.setItem(key, value);
    else window.sessionStorage.removeItem(key);
  } catch {
    // Storage unavailable (private window, quota): the draft still lives
    // in component state; only reload recovery is lost.
  }
}

export interface NotesDockProps {
  kind: NoteEntitySegment;
  entityId: string;
}

export function NotesDock({ kind, entityId }: NotesDockProps) {
  const { user } = useSession();
  // Shared with the Documents dock (X1): only one of the two is open at a
  // time; while the other has the floor this tab renders minimized.
  const { isOpen: open, otherOpen, setOpen } = useDock("notes");
  const toast = useToast();
  const [notes, setNotes] = useState<Note[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [draft, setDraft] = useState("");
  // One box for a note AND a touch (2026-09-10, Brian Ream: "Isn't
  // logging a touch how you enter in notes?"). The header's "Add a note"
  // and this tab are the two entrances to the same act, so this tab
  // carries the same optional chip row. A touch is always written on the
  // household; the notes list tells us which one this record belongs to.
  const [householdId, setHouseholdId] = useState<string | null>(null);
  const [touchType, setTouchType] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  // What the last touch was, kept on screen (a toast alone let Nate think
  // his text had vanished: a touch never appears in this tab's note lists).
  const [loggedTouch, setLoggedTouch] = useState<string | null>(null);
  const [piiAcknowledged, setPiiAcknowledged] = useState(false);
  const [chips, setChips] = useState<FollowupSuggestion[]>([]);
  const [query, setQuery] = useState("");
  // Bumped after a voice note saves — that composer writes server-side
  // and hands back nothing to prepend, so the list refetches.
  const [refreshKey, setRefreshKey] = useState(0);
  // Which notes the history pane lists. Defaults to the WHOLE household
  // (Andrea, 2026-10-12): a record's own list was usually empty while the
  // note she needed sat on the opportunity behind it, so the dock looked
  // like it had no notes at all. "This record" is one click away and is
  // where editing and pinning happen.
  const [scope, setScope] = useState<"household" | "record">("household");
  const navigate = useNavigate();
  const [latestAtBottomChoice, setLatestAtBottomChoice] = useUserSetting(NOTES_LATEST_AT_BOTTOM);
  const latestAtBottom = resolveLatestNotesAtBottom(
    latestAtBottomChoice,
    user?.editable_company_slugs,
  );
  const composerRef = useRef<HTMLTextAreaElement | null>(null);
  // The id this exact draft will be saved under. Held across "Try again"
  // so a save that landed but never answered comes back as already saved,
  // not as a second copy. Dropped the moment the words change.
  const pendingNoteIdRef = useRef<string | undefined>(undefined);
  const draftKey = dockDraftKey(kind, entityId);

  const noteEntityType = useMemo(() => NOTE_KIND_BY_SEGMENT[kind], [kind]);
  const label = ENTITY_LABEL[kind];

  // The household-wide count the closed tab shows. A COUNT over the whole
  // household, so the badge answers "is there anything written about this
  // family?" before the dock is even opened.
  const anchorForCount = useMemo(
    () => ({ entityType: noteEntityType, entityId }),
    [noteEntityType, entityId],
  );
  const { total: householdTotal, householdId: totalHouseholdId } =
    useHouseholdNoteTotal(anchorForCount);
  const seeAllHouseholdId = householdId ?? totalHouseholdId;

  // A different record means a different notebook: close, and drop any
  // half-typed draft rather than carrying it onto someone else's record
  // — the one way a dock like this could do real damage.
  useEffect(() => {
    setOpen(false);
    setNotes(null);
    setDraft(readDockDraft(dockDraftKey(kind, entityId)));
    pendingNoteIdRef.current = undefined;
    setTouchType(null);
    setHouseholdId(null);
    setChips([]);
    setQuery("");
    setPiiAcknowledged(false);
    setSaveError(null);
    setLoggedTouch(null);
    setLoadError(null);
    setScope("household");
  }, [kind, entityId]);

  // Fetch lazily — the whole point of a collapsed tab is that a record
  // page costs nothing until someone actually wants their notes.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    api
      .listEntityNotes(kind, entityId)
      .then((r) => {
        if (cancelled) return;
        setNotes(r.notes);
        setHouseholdId(r.household_id ?? null);
      })
      .catch((err) => {
        if (cancelled) return;
        setLoadError(err instanceof ApiError ? err.message : "Couldn't load notes.");
        setNotes([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open, kind, entityId, refreshKey]);

  // Escape closes — the one modal-ish affordance a non-modal dock still
  // owes the keyboard. Bound only while open so it can't swallow Escape
  // from a modal opened on top of it.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  useEffect(() => {
    writeDockDraft(draftKey, draft);
  }, [draftKey, draft]);

  const openDock = useCallback(() => {
    setOpen(true);
    // Focus the composer on open: the reason you clicked is almost always
    // to write, and this is a non-modal surface so nothing else steals it.
    window.setTimeout(() => composerRef.current?.focus(), 0);
  }, []);

  // Tell any page still rendering this record's notes (the Household and
  // Contact Notes tabs read them out of their own detail payload) that
  // its copy is now stale.
  const notifyChanged = useCallback(
    () => emitNotesChanged({ entityType: noteEntityType, entityId }),
    [noteEntityType, entityId],
  );

  const canModify = (n: Note): boolean =>
    (n.author_user_id != null && n.author_user_id === user?.id) ||
    user?.employee_role_slug === "system_admin";

  async function saveNote() {
    const body = draft.trim();
    const touch = touchType ? TOUCH_OPTIONS.find((o) => o.type === touchType) : null;
    if (body.length === 0 && !touch) return;
    setSaving(true);
    setSaveError(null);
    setLoggedTouch(null);
    try {
      if (touch && householdId) {
        // A touch: the retention-touch activity on the household, with
        // the note as its body — the same write the header's "Add a
        // note" makes. It is an activity, not a note, so it shows in
        // Notes & meetings and "Last touch", not in this tab's list.
        // Names the record the dock is open on, as the header's "Add a
        // note" does, so the touch counts for that record and its company.
        const touchRecord = TOUCH_RECORD_BY_SEGMENT[kind];
        await api.logHouseholdTouch(householdId, {
          activity_type: touch.type,
          ...(touchRecord ? { record_type: touchRecord, record_id: entityId } : {}),
          ...(body ? { note: body } : {}),
        });
        toast.success(
          `${touch.label} logged. It shows in Notes & meetings and updates "Last touch."`,
        );
        setDraft("");
        setTouchType(null);
        setPiiAcknowledged(false);
        setLoggedTouch(touch.label);
        return;
      }
      pendingNoteIdRef.current ??= newNoteId();
      const res = await api.addEntityNote(kind, entityId, {
        body,
        ...(pendingNoteIdRef.current ? { id: pendingNoteIdRef.current } : {}),
      });
      pendingNoteIdRef.current = undefined;
      // A replayed save hands back a note the list may already hold.
      setNotes((prev) => [res.note, ...(prev ?? []).filter((n) => n.id !== res.note.id)]);
      notifyChanged();
      setDraft("");
      setPiiAcknowledged(false);
      // Say where it went (Andrea, 2026-10-12: "she is not sure where
      // notes are"). A note belongs to the record it was written on.
      toast.success(
        `Note saved to ${label}.`,
        seeAllHouseholdId
          ? {
              action: {
                label: "See all household notes",
                onClick: () => navigate(`/households/${encodeURIComponent(seeAllHouseholdId)}?tab=notes`),
              },
            }
          : undefined,
      );
      // Fire the extraction; failures (budget, AI off) stay silent — the
      // note itself is already safely saved.
      try {
        const ex = await api.extractFollowupSuggestions("note", res.note.id);
        setChips(ex.suggestions ?? []);
      } catch {
        setChips([]);
      }
    } catch (err) {
      setSaveError(noteSaveFailureMessage(err));
    } finally {
      setSaving(false);
    }
  }

  const sorted = (notes ?? []).slice().sort((a, b) => {
    if (a.pinned !== b.pinned) return a.pinned ? -1 : 1;
    if (a.important !== b.important) return a.important ? -1 : 1;
    return a.created_at < b.created_at ? 1 : -1;
  });

  // Client-side filter, deliberately: these are ONE record's notes, not
  // the household-wide archive (that has its own server-side search at
  // GET /api/household-notes). A record's own list is small enough that
  // a round-trip per keystroke would be the slower answer.
  const needle = query.trim().toLowerCase();
  const visible = needle
    ? sorted.filter(
        (n) =>
          n.body.toLowerCase().includes(needle) ||
          (n.title ?? "").toLowerCase().includes(needle),
      )
    : sorted;

  const count = notes?.length ?? 0;

  if (typeof document === "undefined") return null;

  if (!open) {
    return createPortal(
      <button
        type="button"
        className={`notes-dock-tab${otherOpen ? " notes-dock-tab--min" : ""}`}
        onClick={otherOpen ? undefined : openDock}
        aria-disabled={otherOpen || undefined}
        title={
          otherOpen
            ? "Close Documents first"
            : householdTotal !== null
              ? `${householdTotal} ${householdTotal === 1 ? "note" : "notes"} across this household. Open to read or add one.`
              : "Open to read or add a note"
        }
        data-testid="notes-dock-tab"
      >
        <NotebookPen size={15} strokeWidth={1.6} aria-hidden />
        <span>Notes{householdTotal !== null && householdTotal > 0 ? ` (${householdTotal})` : ""}</span>
      </button>,
      document.body,
    );
  }

  return createPortal(
    <section
      className="notes-dock"
      aria-label={`Notes on ${label}`}
      data-testid="notes-dock-panel"
    >
      <header className="notes-dock-header">
        <h2 className="notes-dock-title">
          <NotebookPen size={15} strokeWidth={1.6} aria-hidden />
          Notes
          <span className="notes-dock-count">
            {notes === null
              ? "…"
              : householdTotal !== null
                ? `${count} on ${label} · ${householdTotal} in the household`
                : `${count} on ${label}`}
          </span>
        </h2>
        <button
          type="button"
          className="notes-dock-close"
          onClick={() => setOpen(false)}
          aria-label="Close notes"
          title="Close notes (Esc)"
        >
          <ChevronDown size={16} strokeWidth={1.8} aria-hidden />
        </button>
      </header>

      <div className="notes-dock-body">
        {/* LEFT — a place to write. */}
        <div className="notes-dock-pane notes-dock-pane--write">
          {householdId ? (
            <div className="notes-dock-touch">
              <div className="add-note__label-row">
                <h4 className="add-note__label">What kind of touch was this?</h4>
                <span className="add-note__label-hint">Optional</span>
              </div>
              <div className="add-note__chips" role="group" aria-label="Kind of touch">
                {TOUCH_OPTIONS.map((o) => {
                  const on = touchType === o.type;
                  return (
                    <button
                      key={o.type}
                      type="button"
                      className={`add-note__chip${on ? " add-note__chip--on" : ""}`}
                      aria-pressed={on}
                      onClick={() => setTouchType(on ? null : o.type)}
                    >
                      <i aria-hidden />
                      {o.label}
                    </button>
                  );
                })}
              </div>
              {touchType ? (
                <p className="add-note__hint">
                  Saves as a{" "}
                  <strong>
                    {TOUCH_OPTIONS.find((o) => o.type === touchType)?.label.toLowerCase()}
                  </strong>{" "}
                  on the household — counts toward relationship health and updates
                  &ldquo;Last touch.&rdquo;
                </p>
              ) : null}
            </div>
          ) : null}
          <p className="notes-dock-saves-to" data-testid="notes-dock-saves-to">
            {touchType && householdId ? (
              <>
                Saving to <strong>this household</strong> as a touch, so it shows in Notes &amp;
                meetings rather than in the notes list.
              </>
            ) : (
              <>
                Saving to <strong>{label}</strong>. Everyone on the team can read it under
                &ldquo;Whole household.&rdquo;
              </>
            )}
          </p>
          {loggedTouch ? (
            <p className="notes-dock-saves-to" role="status" data-testid="notes-dock-touch-logged">
              <strong>{loggedTouch} logged</strong> as a touch on this household. It is not in the
              notes lists here. Read it in Notes &amp; meetings.{" "}
              {seeAllHouseholdId ? (
                <button
                  type="button"
                  className="notes-dock-pref-link"
                  onClick={() =>
                    navigate(`/households/${encodeURIComponent(seeAllHouseholdId)}?tab=notes`)
                  }
                >
                  See it in the household
                </button>
              ) : null}
            </p>
          ) : null}
          <textarea
            ref={composerRef}
            className="notes-dock-textarea"
            value={draft}
            onChange={(e) => {
              setDraft(e.target.value);
              pendingNoteIdRef.current = undefined;
              setPiiAcknowledged(false);
            }}
            maxLength={8000}
            placeholder={`Add a note about ${label} — what was discussed, what you promised, what's next.`}
          />
          {saveError && (
            <div className="notes-dock-error" role="alert">
              {saveError}{" "}
              <Button size="sm" disabled={saving} onClick={() => void saveNote()}>
                Try again
              </Button>
            </div>
          )}
          <NotePiiWarning text={draft} />
          <div className="notes-dock-actions">
            <Button
              variant="sw-primary"
              disabled={
                saving ||
                (draft.trim().length === 0 && !touchType) ||
                (draftHasPii(draft) && !piiAcknowledged)
              }
              onClick={() => void saveNote()}
            >
              {saving ? "Saving…" : "Save note"}
            </Button>
            {draftHasPii(draft) && !piiAcknowledged && (
              <Button size="sm" onClick={() => setPiiAcknowledged(true)}>
                Save it here anyway
              </Button>
            )}
          </div>
          <FollowupSuggestionChips suggestions={chips} />
          {/* Or speak it — record → Whisper → AI-structured note + tasks.
              Moves in unchanged; it already accepted all six kinds. */}
          <VoiceNoteComposer
            entityType={noteEntityType}
            entityId={entityId}
            onSaved={() => {
              setRefreshKey((k) => k + 1);
              notifyChanged();
            }}
          />
        </div>

        {/* RIGHT — the history. The short AI summary belongs at the top of
            this pane (design doc §9); it lands in the follow-on PR, which
            needs its own summary cache table (the existing
            ai_relationship_summaries CHECK admits household/contact only). */}
        <div className="notes-dock-pane notes-dock-pane--history">
          <div className="notes-dock-scope" role="group" aria-label="Which notes to show">
            <button
              type="button"
              className={`notes-dock-scope-btn${scope === "household" ? " is-on" : ""}`}
              aria-pressed={scope === "household"}
              onClick={() => setScope("household")}
            >
              Whole household{householdTotal !== null ? ` (${householdTotal})` : ""}
            </button>
            <button
              type="button"
              className={`notes-dock-scope-btn${scope === "record" ? " is-on" : ""}`}
              aria-pressed={scope === "record"}
              onClick={() => setScope("record")}
            >
              {label.charAt(0).toUpperCase() + label.slice(1)}
              {notes !== null ? ` (${count})` : ""}
            </button>
          </div>

          <label className="notes-dock-search">
            <Search size={14} strokeWidth={1.6} aria-hidden />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={scope === "household" ? "Search the household's notes" : "Search these notes"}
              aria-label={scope === "household" ? "Search the household's notes" : "Search these notes"}
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery("")}
                aria-label="Clear search"
                className="notes-dock-search-clear"
              >
                <X size={13} strokeWidth={1.8} aria-hidden />
              </button>
            )}
          </label>

          {loadError && <div className="notes-dock-error">{loadError}</div>}

          {scope === "household" ? (
            <HouseholdScopeList
              anchor={anchorForCount}
              query={query}
              onEditHere={() => {
                setQuery("");
                setScope("record");
              }}
            />
          ) : (
            <>
              {notes === null ? (
                <p className="notes-dock-empty">Loading…</p>
              ) : visible.length === 0 ? (
                <p className="notes-dock-empty">
                  {count === 0
                    ? `No notes on ${label} yet. Write the first one on the left, or switch to "Whole household" to read notes saved on other records.`
                    : `No notes match "${query.trim()}".`}
                </p>
              ) : (
                <ul className="household-tab-note-list">
                  {visible.map((n) => (
                    <NoteRow
                      key={n.id}
                      note={n}
                      kind={kind}
                      entityId={entityId}
                      canModify={canModify(n)}
                      onUpdated={(updated) => {
                        setNotes((prev) =>
                          (prev ?? []).map((x) => (x.id === updated.id ? updated : x)),
                        );
                        notifyChanged();
                      }}
                      onDeleted={(noteId) => {
                        setNotes((prev) => (prev ?? []).filter((x) => x.id !== noteId));
                        notifyChanged();
                      }}
                    />
                  ))}
                </ul>
              )}
              <RelatedHouseholdNotes anchor={{ entityType: noteEntityType, entityId }} />
            </>
          )}

          {/* A personal preference, offered where the person is looking at
              a case (see NOTES_LATEST_AT_BOTTOM for why it is per-user). */}
          {kind === "service-records" && (
            <button
              type="button"
              className="notes-dock-pref-link"
              onClick={() => setLatestAtBottomChoice(!latestAtBottom)}
            >
              {latestAtBottom
                ? "Hide \u201CLatest notes\u201D at the bottom of case pages"
                : "Show \u201CLatest notes\u201D at the bottom of case pages"}
            </button>
          )}
        </div>
      </div>
    </section>,
    document.body,
  );
}

const SCOPE_PAGE_SIZE = 25;
// The endpoint clamps `limit` to 100; past that the household Notes tab pages.
const SCOPE_MAX = 100;

/** The dock's "Whole household" list. A child component so the household
 *  query only runs while it is on screen: the dock itself stays mounted
 *  (as a closed tab) on every record page and must cost nothing there. */
function HouseholdScopeList({
  anchor,
  query,
  onEditHere,
}: {
  anchor: { entityType: HouseholdNoteKind; entityId: string };
  query: string;
  onEditHere: () => void;
}) {
  const [limit, setLimit] = useState(SCOPE_PAGE_SIZE);
  const [debounced, setDebounced] = useState(query.trim());

  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(query.trim()), 250);
    return () => window.clearTimeout(t);
  }, [query]);
  // A new search starts from the top, not from wherever "Show more" had got to.
  useEffect(() => setLimit(SCOPE_PAGE_SIZE), [debounced]);

  const { page, loading, error, reload } = useHouseholdNotes(anchor, {
    kinds: [],
    q: debounced,
    limit,
    offset: 0,
  });
  useEffect(
    () => subscribeNotesChanged({ entityType: anchor.entityType, entityId: anchor.entityId }, reload),
    [anchor.entityType, anchor.entityId, reload],
  );

  if (error) return <div className="notes-dock-error">{error}</div>;
  if (!page) return <p className="notes-dock-empty">Loading…</p>;
  if (page.total === 0) {
    return (
      <p className="notes-dock-empty">
        {debounced
          ? `No notes in this household match "${debounced}".`
          : "No notes have been written about this household yet. Write the first one on the left."}
      </p>
    );
  }
  return (
    <>
      <ul className="household-tab-note-list">
        {page.notes.map((n) => (
          <HouseholdNoteRow
            key={n.id}
            note={n}
            {...(n.entity_type === anchor.entityType && n.entity_id === anchor.entityId
              ? { onEditHere }
              : {})}
          />
        ))}
      </ul>
      {page.total > page.notes.length && (
        <p style={{ fontSize: 12.5, margin: "8px 0" }}>
          Showing {page.notes.length} of {page.total}.{" "}
          {limit >= SCOPE_MAX ? "Open the household Notes tab to read the rest." : (
          <button
            type="button"
            className="notes-dock-pref-link"
            disabled={loading}
            onClick={() => setLimit((l) => Math.min(l + SCOPE_PAGE_SIZE, SCOPE_MAX))}
          >
            Show more
          </button>
          )}
        </p>
      )}
    </>
  );
}
